<template>
  <div
    class="h-100 d-flex flex-column"
    style="overflow: hidden"
  >
    <div>
      <div class="d-flex justify-content-between align-items-center">
        <div class="page-header">
          <h1>
            {{ $t('shop.addOrders') }}
          </h1>
        </div>
        <div>
          <button
            type="button"
            aria-label="Close"
            class="close"
            @click="cancel"
          >
            ×
          </button>
        </div>
      </div>

      <hr class="mt-1">
      <div v-show="!summary">
        <div
          v-if="dailyOrderSchema || monthlyOrderSchema"
          v-show="!isSmallStep || calendarStep"
        >
          <div class="d-flex justify-content-between">
            <div style="width: 100px" />
            <div style="width: 300px; ">
              <Calendar
                v-if="dailyOrderSchema"
                multi-select
                compressed
                :available-days="Object.keys(listingByDay)"
                :day-status="dayStatus"
                style="height: 350px"
                @highlighted="selectedDays = $event.list"
                @dates="requestCalendar"
              />
              <YearMonthCalendar
                v-else
                style="height: 300px"
                :available="Object.keys(listingByDay)"
                :highlighted="selectedDays"
                @highlighted="selectedDays = $event.list"
                @update:dates="requestCalendar"
              />
            </div>
            <div class="d-flex">
              <div class="d-flex justify-content-end mr-4">
                <ReportCheckbox
                  v-model="ignoreLimit"
                  :text="$t('shop.ignoreLimit')"
                />
                <ReportCheckbox
                  v-if="monthlyOrderSchema"
                  v-model="createSubscription"
                  class="ml-2"
                  :text="$t('shop.createSubscription')"
                />
              </div>
              <div class="text-right">
                <button
                  class="btn btn-primary btn-lg next-btn mr-1"
                  :disabled="!dateSelected"
                  @click="onCalendarNext"
                >
                  {{ $t('general.next') }}
                  <i class="fas fa-arrow-right px-2" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="d-flex justify-content-end mb-3"
        >
          <div
            style="width: 200px"
            class="text-right"
          >
            <button
              class="btn btn-primary btn-lg next-btn mr-1"
              @click="summary = true"
            >
              {{ $t('general.next') }}
              <i class="fas fa-arrow-right px-2" />
            </button>
          </div>
        </div>

        <hr v-if="(dailyOrderSchema || monthlyOrderSchema) && (!isSmallStep || calendarStep)">
        <div
          v-if="isSmallStep && !calendarStep"
          class="d-flex justify-content-between mb-2"
        >
          <button
            class="btn btn-secondary btn-lg next-btn mr-1"
            @click="calendarStep = true"
          >
            <i class="fas fa-arrow-left px-2" />
            {{ $t('general.back') }}
          </button>
          <div
            v-if="selectedDays.length > 0"
            class="d-flex align-items-center mx-2"
            style="overflow-x: auto; flex: 1; flex-wrap: nowrap;"
          >
            <span
              v-for="day in selectedDays"
              :key="day"
              class="badge badge-pill badge-primary mr-1 flex-shrink-0"
              style="font-size: 12px; padding: 5px 8px;"
            >
              {{ formatSelectedDay(day) }}
            </span>
          </div>
          <button
            class="btn btn-primary btn-lg next-btn mr-1"
            :disabled="!dateSelected"
            @click="summary = true"
          >
            {{ $t('general.next') }}
            <i class="fas fa-arrow-right px-2" />
          </button>
        </div>
        <hr v-if="(dailyOrderSchema || monthlyOrderSchema) && (isSmallStep && !calendarStep)">
        <div
          v-show="!isSmallStep || !calendarStep"
          class="row"
        >
          <div class="col-7">
            <OrderPayersList
              :catalog-id="catalogId"
              :selected.sync="payers"
              :product-ids="selectedProducts"
              :selected-listings.sync="selectedListings"
              :selected-days="selectedDays"
              :style="{
                overflowY: 'auto',
                height: payersListHeight
              }"
              @orderCount="orderCount = $event"
              @offer="offer = $event"
            />
          </div>
          <div class="col-5">
            <ProductList
              :catalog-id="catalogId"
              :selected.sync="selectedProducts"
              :selected-listings.sync="selectedListings"
              :with-calendar="dailyOrderSchema || monthlyOrderSchema"
            />
          </div>
        </div>
      </div>
      <template v-if="summary">
        <div class="d-flex justify-content-between">
          <div
            style="width: 200px"
            class="text-left"
          >
            <button
              class="btn btn-secondary btn-lg next-btn mr-1"
              @click="summary = false"
            >
              {{ $t('general.back') }}
              <i class="fas fa-arrow-left px-2" />
            </button>
          </div>
          <div
            style="width: 200px"
            class="text-right"
          >
            <button
              class="btn btn-primary btn-lg next-btn mr-1"
              :disabled="pending"
              @click="save"
            >
              {{ $t('general.save') }}
              <i class="fas fa-check px-2" />
            </button>
          </div>
        </div>
        <hr>
        <BTextarea
          v-model="comment"
          :placeholder="`${$t('shop.comment')}...`"
          class="px-2"
        />
        <hr>
        <div
          style="height: calc(100vh - 325px); overflow-y: auto;"
        >
          <OrderByDaySummary
            v-if="catalogDetails?.orderSchema === 'Daily'
              || catalogDetails?.orderSchema === 'Monthly'"
            :payers="payers"
            :selected-days="selectedDays"
            :order-count="orderCount"
            :calendar="calendar"
            :offer="offer"
            :order-schema="catalogDetails?.orderSchema"
            :monthly="catalogDetails?.orderSchema === 'Monthly'"
            @orders="orders = $event"
          />
          <OrderSingleSummary
            v-else
            :payers="payers"
            :offer="offer"
            @orders="orders = $event"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Calendar from '@/modules/payer/components/shop/Calendar';
import YearMonthCalendar from '@/modules/payer/components/shop/YearMonthCalendar';
import ReportCheckbox from '@/components/reports/ReportCheckbox';
import { mapActions, mapGetters } from 'vuex';
import OrderByDaySummary from './OrderByDaySummary';
import OrderPayersList from './OrderPayersList';
import OrderSingleSummary from './OrderSingleSummary';
import ProductList from './ProductList';

export default {
  props: {
    catalogId: String,
  },
  data: () => ({
    payers: [],
    selectedDays: [],
    selectedProducts: [],
    selectedListings: [],
    availableDays: [],
    summary: false,
    calendarStep: true,
    isSmallScreen: false,
    createSubscription: false,
    offer: [],
    calendar: [],
    orders: [],
    orderCount: {},
    comment: '',
    ignoreLimit: true,
    pending: false,
  }),
  components: {
    Calendar,
    YearMonthCalendar,
    ReportCheckbox,
    ProductList,
    OrderPayersList,
    OrderByDaySummary,
    OrderSingleSummary,
  },
  computed: {
    ...mapGetters('shop', [
      'catalogDetails',
      'dailyOrderSchema',
      'monthlyOrderSchema',
    ]),
    isSmallStep() {
      return this.isSmallScreen && (this.dailyOrderSchema || this.monthlyOrderSchema);
    },
    payersListHeight() {
      if (!this.dailyOrderSchema && !this.monthlyOrderSchema) {
        return 'calc(100vh - 210px)';
      }
      if (this.isSmallStep) {
        return 'calc(100vh - 280px)';
      }
      return 'calc(100vh - 510px)';
    },
    dateSelected() {
      if (this.dailyOrderSchema || this.monthlyOrderSchema) {
        return this.selectedDays.length > 0;
      }
      return true;
    },
    listingByDay() {
      return this.calendar
        .reduce((acc, curr) => {
          if (!acc[curr.day]) {
            acc[curr.day] = [];
          }
          acc[curr.day].push(curr.listingId);
          return acc;
        }, {});
    },
    dayStatus() {
      return Object.entries(this.listingByDay)
        .reduce((acc, [day, listingIds]) => {
          if (!this.selectedDays.includes(day)) {
            return acc;
          }

          const count = this.offer
            .flatMap((y) => y.listingIds)
            .filter((lid) => listingIds.includes(lid))
            .length;

          acc[day] = {
            // waiting: totalCount - count,
            paid: count,
          };

          return acc;
        }, {});
    },
  },
  methods: {
    ...mapActions('shop', [
      'getProducts',
      'getCatalogListings',
      'getShopCatalog',
      'getListingsCalendar',
      'updateOrders',
    ]),
    save() {
      this.pending = true;

      this.updateOrders({
        params: {
          query: {
            catalogId: this.catalogId,
            ignoreLimit: this.ignoreLimit,
          },
        },
        data: {
          createSubscription: this.createSubscription,
          addedItems: this.orders
            .map((x) => ({
              ...x,
              count: this.orderCount[x.payerId] || 1,
              availabilityId: x.listingId,
              comment: this.comment,
            })),
        },
      })
        .then(() => {
          this.$router.back();
        })
        .finally(() => {
          this.pending = false;
        });
    },
    formatSelectedDay(day) {
      if (this.monthlyOrderSchema) {
        const [year, month] = day.split('-');
        return new Date(year, month - 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
      }
      const [year, month, date] = day.split('-');
      return new Date(year, month - 1, date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    },
    onCalendarNext() {
      if (this.isSmallStep) {
        this.calendarStep = false;
      } else {
        this.summary = true;
      }
    },
    onResize() {
      this.isSmallScreen = window.innerHeight < 1000;
    },
    cancel() {
      this.$router.back();
    },
    requestCalendar({ from, to }) {
      this.getListingsCalendar({
        params: {
          catalogId: this.catalogId,
          query: {
            from,
            to,
          },
        },
      })
        .then(({ data }) => {
          this.calendar = data;
        });
    },
    request() {
      if (!this.listings || this.listings.length === 0) {
        this.getCatalogListings({
          params: {
            catalogId: this.catalogId,
          },
        });
      }
      if (!this.products || this.products.length === 0) {
        this.getProducts({
          params: {
            catalogId: this.catalogId,
          },
        });
      }

      if (!this.catalogDetails) {
        this.getShopCatalog({
          params: {
            catalogId: this.catalogId,
          },
        });
      }
    },
  },
  mounted() {
    this.isSmallScreen = window.innerHeight < 1000;
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  created() {
    this.request();
    this.$emit('page', 'form');
  },
};
</script>

<style lang="scss" scoped>
  .close {
    font-size: 40px;
  }
</style>
